import type { ParsedUrlQuery } from 'querystring';
import React from 'react';

import type { GetStaticPaths, GetStaticProps, Redirect } from 'next';
import { isAddress } from 'viem/utils';
import { BURNED_ADDRESSES } from '@soundxyz/utils/const';
import { parseEns } from '@soundxyz/utils/ens';
import type { ProfileSectionType } from '@components/Profile/CarouselsExpanded';
import { isProfileSectionType, SectionExpanded } from '@components/Profile/CarouselsExpanded';
import { CollectorHero } from '@components/Profile/ProfileHero/CollectorHero';
import { UserInfo } from '@components/Profile/Sections/UserInfo';
import { ProfileContainer, ProfileWrapper } from '@components/Profile/shared';

import { Seo } from '@components/Seo';
import type { GetUserProfileNewQuery } from '@graphql/reactQuery';
import {
  fetchGQL,
  getFragment,
  GetUserProfileNewDocument,
  useQuery,
  UserProfileFragmentDoc,
  UserSoundCollectionFragmentDoc,
} from '@graphql/reactQuery';
import { getUserOpenGraphImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const ISR_REVALIDATE_PERIOD = 20;

const notFoundPermanentRedirect = {
  redirect: {
    destination: '/404',
    permanent: true,
  },
  revalidate: ISR_REVALIDATE_PERIOD,
};

function staticPropsPublicAddressOrEns({
  params: { publicAddress: publicAddressOrEns } = {},
}: {
  params: ParsedUrlQuery | undefined;
}):
  | ({ redirect: Redirect; revalidate: number | boolean } & { variables?: undefined })
  | {
      redirect?: undefined;
      variables: { publicAddress: string } | { ens: string };
    } {
  if (!publicAddressOrEns || Array.isArray(publicAddressOrEns)) {
    return notFoundPermanentRedirect;
  }

  if (isAddress(publicAddressOrEns)) {
    if (BURNED_ADDRESSES.has(publicAddressOrEns.toLowerCase())) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: false,
      };
    }

    return {
      variables: {
        publicAddress: publicAddressOrEns.toLowerCase(),
      },
    };
    // If it isn't an address, maybe it is an ENS name
  } else {
    const ens = parseEns(publicAddressOrEns);

    // Ensure it is an ENS name
    if (!ens) return notFoundPermanentRedirect;

    return {
      variables: {
        ens,
      },
    };
  }
}

interface PageProps {
  user: NonNullable<GetUserProfileNewQuery['userByAddress']>;
  profileSection: ProfileSectionType;
}

export const getStaticProps: GetStaticProps = async ({ params = {} }) => {
  const { profileSection } = params;

  const staticProps = staticPropsPublicAddressOrEns({
    params,
  });
  const { redirect, variables } = staticProps;

  if (redirect) return { redirect, revalidate: staticProps.revalidate };

  if (!isProfileSectionType(profileSection)) {
    return {
      notFound: true,
    };
  }

  return Promise.all([
    fetchGQL(GetUserProfileNewDocument, {
      variables,
    }),
  ]).then(async ([res]) => {
    if (!res.data.userByAddress) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: ISR_REVALIDATE_PERIOD,
      };
    }

    // Artists should be redirected to their profile page
    const user = getFragment(UserProfileFragmentDoc, res.data.userByAddress);

    if (user.artist && user.artist.soundHandle && profileSection) {
      return {
        redirect: {
          destination: webappUriPath(user.artist.webappUri, {
            subpath: `/${profileSection}`,
          }),
          permanent: false,
          revalidate: ISR_REVALIDATE_PERIOD,
        },
      };
    }

    if (user.artist && user.artist.soundHandle) {
      return {
        redirect: {
          destination: webappUriPath(user.artist.webappUri),
          permanent: false,
          revalidate: ISR_REVALIDATE_PERIOD,
        },
      };
    }

    const props: PageProps = {
      user: res.data.userByAddress,
      profileSection,
    };

    return {
      props,
      revalidate: ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default function PublicUserProfile({ user: userProp, profileSection }: PageProps) {
  const { data } = useQuery(GetUserProfileNewDocument, {
    variables: {
      ens: userProp.ens || undefined,
      publicAddress: userProp.publicAddress,
    },
    initialData() {
      return {
        data: {
          userByAddress: userProp,
        },
      };
    },
    staleTime: 0,
    cacheTime: 0,
  });

  const user = getFragment(UserProfileFragmentDoc, data?.data.userByAddress || userProp);

  const userCollectorFragment = getFragment(UserSoundCollectionFragmentDoc, user);

  const username =
    user.twitterHandle || `${user.publicAddress?.slice(0, 5)}...${user.publicAddress?.slice(-4)}`;

  const userOg = getUserOpenGraphImage({ userId: user.id });

  return (
    <>
      <Seo
        title={`${username}'s Sound profile`}
        twitterHandle={user.twitterHandle}
        smallTwitterCard={!userOg && !!user.avatar?.url}
        description={user.description}
        image={userOg || user.avatar?.url}
      />
      <ProfileContainer>
        <CollectorHero collectorData={user} />
        <ProfileWrapper>
          <UserInfo userData={userCollectorFragment} />
          <SectionExpanded user={user} profileSection={profileSection} artist={null} />
        </ProfileWrapper>
      </ProfileContainer>
    </>
  );
}
